import React, { useState, useEffect } from 'react';
import { Grid, makeStyles, Container, Tabs, Tab } from '@material-ui/core';
import { injectIntl, FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { getAllLetters, fetchServices, updateServiceDetails } from 'app/store/slices/services';

import LetterSelector from './components/LetterSelector';
import CardHolder from 'app/components/CardHolder';
import Breadcrumb from 'app/components/BreadCrumb';
import IndexedServices from './components/IndexedServices';
import TemporalServices from './components/TemporalServices';

import { hideLoading, showLoading } from 'app/store/slices/settings';
import { errorHandler } from 'app/utils/errorHandler';
import { SERVICES_PROPERTIES } from 'app/config/services';

import styles from './styles';
const useStyles = makeStyles(styles);

function Services(props) {
    const dispatch = useDispatch();
    const classes = useStyles();
    const history = useHistory();
    const services = useSelector((state) => state.services.services);
    const user = useSelector((state) => state.auth.user);

    const viewAlphabet = 0;
    const viewLastOne = 1;
    const viewMostPopular = 2;

    const [letters, setLetters] = useState([]);
    const [view, setView] = useState(viewAlphabet);
    const [searchText, setSearchText] = useState('');
    const [selectedLetter, setSelectedLetter] = useState('');
    const { intl } = props;

    const breadCrumb = [
        {
            title: intl.formatMessage({ id: 'LABELS.COMMONS.SERVICES' }),
            path: '',
        },
    ];

    useEffect(() => {
        dispatch(showLoading());
        (async () => {
            try {
                await dispatch(fetchServices(user));
            } catch (error) {
                errorHandler(error.response?.status, error.response?.data?.message);
            }
            dispatch(hideLoading());
        })();
    }, [dispatch, user]);

    useEffect(() => {
        setLetters(getAllLetters(services.alphabetic));
    }, [services]);

    useEffect(() => {
        (async () => {
            const promises = [];
            if (services.alphabetic?.length === 0) {
                promises.push(dispatch(fetchServices(user)));
            }
            if (promises.length > 0) {
                await Promise.all(promises);
            }
        })();
        // eslint-disable-next-line
    }, [dispatch, history, user]);

    const handleViewChange = (event, value) => {
        setView(value);
    };

    const onSearchService = (text) => {
        setSearchText(text);
        handleViewChange(null, viewAlphabet);
    };

    const handleLetterToggle = (letter) => {
        if (letters.includes(letter)) {
            let newSelectedLetter = letter;
            selectedLetter === letter && (newSelectedLetter = '');
            setSelectedLetter(newSelectedLetter);
        }
    };

    const onSelect = (service) => {
        if (service.sgServico) {
            dispatch(updateServiceDetails(service));
            history.push(SERVICES_PROPERTIES[service.cdServico].url);
        }
    };

    const renderAlphabetServices = () => {
        return (
            <Grid container spacing={3}>
                <Grid item xs={12} sm={2}>
                    <LetterSelector
                        letters={letters}
                        selectedLetter={selectedLetter}
                        onToggleLetter={handleLetterToggle}
                    />
                </Grid>
                <Grid item xs={12} sm={10}>
                    <IndexedServices
                        services={services.alphabetic}
                        onSelect={onSelect}
                        selectedLetter={selectedLetter}
                        searchText={searchText}
                        onSearch={onSearchService}
                    />
                </Grid>
            </Grid>
        );
    };

    const renderMostPopularServices = () => {
        return (
            <>
                <TemporalServices
                    items={services.mostPopular}
                    onSelect={onSelect}
                    label={'LABELS.MENU.TABS.MOST_POPULAR'}
                />
            </>
        );
    };

    const renderLastOnesServices = () => {
        return (
            <>
                <TemporalServices items={services.lastOnes} onSelect={onSelect} label={'LABELS.MENU.TABS.LAST_ONES'} />
            </>
        );
    };

    const renderTabs = () => {
        return (
            <Grid container spacing={3} className={classes.tabsContainer}>
                <Tabs value={view} onChange={handleViewChange} indicatorColor="primary" textColor="primary">
                    <Tab className={classes.tabs} label={<FormattedMessage id="LABELS.MENU.TABS.ATOZ" />} />
                    {services?.lastOnes && services?.lastOnes.length > 0 && (
                        <Tab className={classes.tabs} label={<FormattedMessage id="LABELS.MENU.TABS.LAST_ONES" />} />
                    )}
                    {services?.mostPopular && services?.mostPopular.length > 0 && (
                        <Tab className={classes.tabs} label={<FormattedMessage id="LABELS.MENU.TABS.MOST_POPULAR" />} />
                    )}
                </Tabs>
            </Grid>
        );
    };

    return (
        <Container>
            <Breadcrumb breadCrumb={breadCrumb} />
            <Grid container spacing={3} className={classes.containerService}>
                <Grid item xs={12}>
                    <CardHolder
                        title={intl.formatMessage({ id: 'LABELS.MENU.SERVICES.TITLE' })}
                        handleSearch={onSearchService}
                        labelName={intl.formatMessage({
                            id: 'LABELS.MENU.SERVICES.SEARCH_SERVICES',
                        })}>
                        {renderTabs()}
                        <>
                            {view === viewAlphabet && renderAlphabetServices()}
                            {view === viewLastOne && renderLastOnesServices()}
                            {view === viewMostPopular && renderMostPopularServices()}
                        </>
                    </CardHolder>
                </Grid>
            </Grid>
        </Container>
    );
}

export default injectIntl(Services);
